import { logger } from 'lib/hatchd-utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { AuthPageLayout } from 'components/auth-page-layout/auth-page-layout';
import { Layout } from 'components/layout';
import { Button, Heading, Input, Stack, Text } from 'components/ui';
import { ApiClient } from 'lib/api/client';
import { getFormFieldErrorsFromResponse } from 'lib/helpers';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { PATHS } from 'settings/config';
import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints';
import * as yup from 'yup';

const StyledButton = styled(Button)`
  width: 100%;

  @media screen and (min-width: ${breakpoints.medium}) {
    max-width: 308px;
  }
`;

type FormData = {
  contact: string;
};

const schema = yup.object().shape({
  contact: yup
    .string()
    .email('Please enter a valid email address')
    .required('Please enter your email address'),
});

export default function ForgotPassword() {
  const router = useRouter();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: yupResolver(schema) });

  // Reset Password mutation
  const resetPasswordMutation = useMutation((data: FormData) =>
    new ApiClient().authenticationApi.userAuthPasswordResetCreate({
      resetPasswordContactRequest: {
        contact: data.contact,
      },
    })
  );

  const onSubmit = async (data: FormData) => {
    try {
      await resetPasswordMutation.mutateAsync(data);
      router.push(PATHS.auth.forgotPasswordConfirmation);
    } catch (error) {
      if (!axios.isAxiosError(error)) return;

      if (error.response) {
        // Handle server errors
        if (error.response.status === 500) {
          logger.error('500 Error');
          toast.error('Something has gone wrong, please try again later');
        }

        // Handle validation errors
        if (error.response.data.code === 'VALIDATION_ERROR') {
          logger.error('Validation error');

          // Map errors returned to relevant form fields
          const errors = getFormFieldErrorsFromResponse<FormData>(
            error.response.data.errors
          );
          errors.forEach(({ field, message }) => setError(field, { message }));
        }
      }
    }
  };

  return (
    <Layout meta={{ title: 'Forgot Password' }}>
      <AuthPageLayout>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack space={3}>
            <Heading level={'h1'}>Forgot Password</Heading>
            <Text fontSize={2}>
              Please enter your registered email address. We will send you a
              code via email to reset your password.
            </Text>
            <Input
              id='contact'
              type='email'
              label='Email'
              placeholder='johndoe@email.com'
              state={errors.contact && 'error'}
              message={errors.contact?.message}
              {...register('contact')}
            />
            <StyledButton type='submit' isLoading={isSubmitting}>
              Reset Password
            </StyledButton>
          </Stack>
        </form>
      </AuthPageLayout>
    </Layout>
  );
}
